import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";

export const BUDGET_TYPE_LABELS = {
  capex_budget: "Capex",
  opex_budget: "Opex",
};

export const getBudgetTypeLabel = (value) =>
  BUDGET_TYPE_LABELS[value] || value || "";

// STORE role - Department hardcode
// Hav to change it here if they ever decide to use a diff dept
export const STORE_ROLE_BUDGET_CATEGORY_NAME = "Procurement Budget Opex";

export const filterStoreRoleLineItems = (lineItems) =>
  (lineItems || []).filter(
    (li) =>
      (li.category?.name || li.name || "").trim().toLowerCase() ===
      STORE_ROLE_BUDGET_CATEGORY_NAME.toLowerCase()
  );

// Sums an item's per-store allocation intent when present (a multi-store HEAD_OFFICE
// item), otherwise falls back to its flat `quantity` (single-store items, or a multi-store
// item before any per-store entry has run yet) -- the one place "what is this item's
// pooled quantity" is computed, so amount/tax/total previews and the outgoing payload never
// disagree about it.
export const getPooledQuantity = (item) =>
  Array.isArray(item.allocations) && item.allocations.length > 0
    ? item.allocations.reduce((sum, a) => sum + (parseFloat(a.requestedQuantity) || 0), 0)
    : parseFloat(item.quantity) || 0;

// Client-writable allocation fields only -- allocationId/storeId/requestedQuantity express
// per-store intent; reservedQuantity/convertedQuantity/shortClosedQuantity are backend-owned
// (allocation.service.js) and must never be sent (the backend's Joi schema forbids them).
// `quantity` is the same amount entered once and replicated to every store, not divided --
// 2 stores + qty 2 means each store gets requestedQuantity 2, never 1.
export const buildAllocationIntent = (quantity, stores, existingAllocations = []) => {
  const qty = parseFloat(quantity) || 0;
  return (stores || []).map((store) => {
    const storeId = store._id || store.id;
    const existing = existingAllocations.find((a) => String(a.storeId) === String(storeId));
    return {
      allocationId: existing?.allocationId || null,
      storeId,
      requestedQuantity: qty,
    };
  });
};

export const calculateItemAmounts = (item) => {
  const qty = getPooledQuantity(item);
  const rate = parseFloat(item.estimatedRate) || 0;
  const taxRate = parseFloat(item.taxRate) || 0;
  const estimatedAmount = qty * rate;
  const estimatedTaxAmount = (estimatedAmount * taxRate) / 100;
  const estimatedTotalAmount = estimatedAmount + estimatedTaxAmount;
  return { estimatedAmount, estimatedTaxAmount, estimatedTotalAmount };
};

export const getPRTotalAmount = (prItemsByLineItem) =>
  Object.values(prItemsByLineItem || {})
    .flat()
    .reduce((acc, item) => acc + (item.estimatedTotalAmount || 0), 0)
    .toFixed(2);

export const getLineItemTotal = (items) =>
  (items || [])
    .reduce((acc, item) => acc + (item.estimatedTotalAmount || 0), 0)
    .toFixed(2);

export const PR_ITEM_MODEL = {
  item: null,
  itemName: "",
  description: "",
  hsnCode: "",
  uom: "",
  quantity: "",
  estimatedRate: "",
  estimatedAmount: 0,
  taxRate: "",
  estimatedTaxAmount: 0,
  estimatedTotalAmount: 0,
  remarks: "",
  rateContract: false,
  vendorCode: "",
  vendorName: "",
  requiredByDate: "",
  budgetHead: "",
  vendorMappings: [],
  storeCode: "",
  store: null,
  // Per-store allocation intent for a multi-store HEAD_OFFICE item -- see
  // buildAllocationIntent. Empty for single-store items (SELF/SINGLE_STORE), which keep
  // using the flat `quantity` field only.
  allocations: [],
  // The specific categoryMappings pair this row resolved to (see
  // resolveMatchingCategoryMapping / single-store-row.js's auto-populate-items call /
  // add-item-modal.js's Subcategory+Item selection) -- category/subCategory are always the
  // ids buildPayload/buildEditPayload send as-is; categoryMappingId is the item's own
  // stable pair id, sent alongside them as the most unambiguous selector.
  category: undefined,
  subCategory: undefined,
  categoryMappingId: undefined,
  _item: null,
};

// Finds the categoryMappings entry (or, for an item not yet migrated onto categoryMappings,
// its legacy scalar category/subCategory as a single-pair equivalent) whose category is one
// of the given ids -- mirrors the backend's own resolveCategoryMapping/
// backfillCategoryMappings fallback (item.model.js), so a multi-mapping item resolves to the
// specific pair actually relevant to one of the selected budget categories, and a legacy
// single-mapping item without a persisted categoryMappings entry still resolves via its
// deprecated fields.
export const resolveMatchingCategoryMapping = (
  item,
  categoryIds,
  categoryMappingId,
  subCategory
) => {
  const idSet = new Set((categoryIds || []).map(String));

  const mappings =
    item.categoryMappings?.length
      ? item.categoryMappings
      : item.category != null
      ? [{
          mappingId: null,
          category: item.category,
          subCategory: item.subCategory,
        }]
      : [];

  const categoryMatches = mappings.filter((m) =>
    idSet.has(String(m.category?._id ?? m.category?.id ?? m.category))
  );

  if (categoryMatches.length <= 1) {
    return categoryMatches[0] || null;
  }

  if (categoryMappingId) {
    return (
      categoryMatches.find(
        (m) => String(m.mappingId) === String(categoryMappingId)
      ) || null
    );
  }

  if (subCategory != null) {
    return (
      categoryMatches.find(
        (m) =>
          String(m.subCategory?._id ?? m.subCategory?.id ?? m.subCategory) ===
          String(subCategory)
      ) || null
    );
  }

  return categoryMatches[0] || null;
};

export const normalizeCode = (value) => (value ?? "").toString().trim().toUpperCase();

// Concurrency cap for per-row lookups (item resolution is batched up-front by category, but
// vendor-mapping/store-budget checks are per-row) during bulk upload resolution, mirroring
// single-store-row.js's VENDOR_MAPPING_LOOKUP_CONCURRENCY pattern.
const UPLOAD_LOOKUP_CONCURRENCY = 8;

// Same endpoint/status-filter/max-price logic as single-store-row.js / add-item-modal.js.
const fetchApprovedVendorMappings = (itemId, prDate) =>
  invokeApi(
    HTTP_METHODS.GET,
    `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.VENDOR_ITEM_MAPPING}`,
    null,
    {
      item: itemId,
      status: "approved",
      limit: 1000,
      ...(prDate ? { date: prDate } : {}),
    }
  )
    .then((res) => {
      const mappings = Array.isArray(res) ? res : res?.results || [];
      return mappings.filter((m) => m.status === "approved");
    })
    .catch(() => []);

// Item Code can't be filtered server-side (GET_ITEM_LIST has no code param), so items for
// every unique category among the selected budget line items are fetched once up front and
// indexed by normalized Item Code for O(1) row-level lookups.
const resolveItemsByCategories = async (categoryIds) => {
  const itemsByCode = new Map();
  await Promise.all(
    categoryIds.map((catId) =>
      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_ITEM_LIST}`,
        null,
        { page: 1, limit: 1000, category: catId }
      )
        .then((res) => {
          const items = Array.isArray(res) ? res : res?.results || [];
          items.forEach((item) => {
            const code = normalizeCode(item.itemCode);
            if (code) itemsByCode.set(code, item);
          });
        })
        .catch(() => {})
    )
  );
  return itemsByCode;
};

// Subcategories for the selected budget categories, fetched independently the same way
// resolveItemsByCategories fetches items — do NOT rely on the page's own subcategoryMap
// state for this: that's populated by a separate effect (loadSubcategoriesForLineItem)
// that may not have resolved yet by the time an upload happens, which was silently
// leaving _subCategory unresolved (blank Subcategory field) on a race. Same endpoint/
// params index.js's loadSubcategoriesForLineItem uses, indexed by normalized subcategory id.
// Exported so single-store-row.js's SingleStorePRSection can resolve its own rows'
// _subCategory the same race-free way, instead of trusting the parent's subcategoryMap prop.
export const resolveSubCategoriesByCategories = async (categoryIds) => {
  const subCategoriesById = new Map();
  await Promise.all(
    categoryIds.map((catId) =>
      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SUBCATEGORY}`,
        null,
        { page: 1, limit: 1000, category: catId }
      )
        .then((res) => {
          const subCategories = Array.isArray(res) ? res : res?.results || [];
          subCategories.forEach((sc) => {
            const id = sc._id ?? sc.id;
            if (id != null) subCategoriesById.set(String(id), sc);
          });
        })
        .catch(() => {})
    )
  );
  return subCategoriesById;
};

const resolveStoreByCode = (code, storeList = [], codeField = "code") => {
  const target = normalizeCode(code);
  return storeList.find((s) => normalizeCode(s[codeField]) === target) || null;
};

// Location Master is the source of truth for City/State/Region resolution (it's what the
// backend's own resolveStores() queries against) — Store Master is only used to backfill a
// missing city/state/region on a Location record, matched by store code first
// (Location.code <-> Store Master.storeNumber, the same pairing the Store Code column
// resolves against above), falling back to name matching (Location.name <-> Store Master's
// real name field, ownerName) only when the Location has no code to match on at all.
// Metadata is kept per-field and partial: a store missing only one of city/state/region
// (even after backfill) still qualifies for the other two — it's up to each field's own
// option list/resolution (cityOptions/stateOptions/regionOptions, getResolvedStores) to
// only offer/match on a non-empty value for that specific field. Never fall back to the
// raw, unfiltered store list.
export const buildLocationFirstStores = (storeList = [], storeMasterList = []) => {
  return storeList
    .filter((loc) => loc.locationType === "STORE")
    .map((loc) => {
      let city = loc.city || "";
      let state = loc.state || "";
      let region = loc.region || "";
      if (!city || !state || !region) {
        let match = loc.code ? resolveStoreByCode(loc.code, storeMasterList, "storeNumber") : null;
        if (!match && loc.name) {
          const target = normalizeCode(loc.name);
          match = storeMasterList.find((sm) => normalizeCode(sm.ownerName) === target) || null;
        }
        if (match) {
          city = city || match.city || "";
          state = state || match.state || "";
          region = region || match.region || "";
        }
      }
      return { ...loc, city, state, region };
    });
};

// Confirms a specific store (not just the pooled HO set) actually has an approved budget
// line item in the given category — the modal's HO budget check only pools across stores,
// so an individual row's store can still lack budget for the item's category.
const storeBudgetHasCategory = (storeId, categoryId, prType, financialYear) => {
  const budgetType = prType === "OPEX" ? "opex_budget" : "capex_budget";
  return invokeApi(
    HTTP_METHODS.GET,
    `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
    null,
    { page: 1, status: "approved", site: storeId, limit: 1000, budgetType, dropdown: true, financialYear }
  )
    .then((response) => {
      if (response.message || !response.totalResults) return false;
      const budget = response.results[0];
      return (budget.lineItems || []).some(
        (li) =>
          li.status === "approved" &&
          String(li.category?.id ?? li.category?._id ?? li.category) === String(categoryId)
      );
    })
    .catch(() => false);
};

// Runs async row resolvers with bounded concurrency so a large upload doesn't fire hundreds
// of simultaneous requests.
const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  for (let i = 0; i < items.length; i += limit) {
    const chunk = items.slice(i, i + limit);
    const chunkResults = await Promise.all(chunk.map((item, idx) => mapper(item, i + idx)));
    chunkResults.forEach((res, idx) => {
      results[i + idx] = res;
    });
  }
  return results;
};

// Maps the response from the PR Excel-upload endpoint (excel/pr-item-details) into the
// same { [lineItemId]: PRItem[] } shape manual entry uses. The template only carries Item
// Code, Quantity, an optional Remarks, and — for the Stores/Cities/Regions destinations —
// Store Code; everything else (budget category, description, HSN/UOM, rate, tax, and the
// store itself for the Self destination) is resolved here from the item/store master data
// and approved vendor mappings, the same way manual entry (add-item-modal.js) resolves
// them, rather than being read from the uploaded row.
export const mapPrFileUploadResponse = async (
  uploadedData,
  {
    selectedLineItems = [],
    storeList = [],
    storeMasterList = [],
    procurementDestination = "SELF",
    selectedStores = [],
    prType = "",
    financialYear = "",
    hoSiteId = "",
    prDate = "",
    isHeadOffice = false,
  } = {}
) => {
  const rawItems = Array.isArray(uploadedData) ? uploadedData : uploadedData?.items || [];
  const errors = Array.isArray(uploadedData?.errors) ? [...uploadedData.errors] : [];
  const grouped = {};

  const categoryIds = [
    ...new Set(
      selectedLineItems
        .map((li) => li.category?.id ?? li.category?._id ?? li.category)
        .filter((id) => id != null)
    ),
  ];
  const [itemsByCode, subCategoriesById] = await Promise.all([
    resolveItemsByCategories(categoryIds),
    resolveSubCategoriesByCategories(categoryIds),
  ]);
  const storeBudgetCache = new Map();
  const selectedStoreIds = new Set(selectedStores.map((s) => String(s._id || s.id)));

  // Head Office/Self destination has no target store to name — items are for the HO
  // location itself (the site already selected in the create-PR modal), so Store Code
  // isn't read from the row at all; it's resolved once here and assigned to every row.
  const isSelfDestination = procurementDestination === "SELF";
  const hoSite = isSelfDestination
    ? storeList.find((s) => String(s._id || s.id) === String(hoSiteId)) || null
    : null;
  // City/State/Region are resolved against the same Location-first, Store-Master-backfilled
  // list that populated selectedStores for these destinations (buildLocationFirstStores) —
  // Specific Stores stays on the plain Location master (storeList) as before.
  const isCityStateRegion = ["CITY", "STATE", "REGION"].includes(procurementDestination);
  const resolvedHOStores = isCityStateRegion
    ? buildLocationFirstStores(storeList, storeMasterList)
    : [];

  const isDerivingFromUpload = !isSelfDestination && selectedStores.length === 0;
  const singleValueField =
    procurementDestination === "STATE" ? "state" : procurementDestination === "REGION" ? "region" : null;
  let derivedSingleValue = null;
  const derivedStoresById = new Map();

  const resolvedRows = await mapWithConcurrency(rawItems, UPLOAD_LOOKUP_CONCURRENCY, async (row, idx) => {
    const rowNumber = row.rowNumber || row.row || idx + 2; // +2: header row is row 1
    const fail = (message) => {
      errors.push({ row: rowNumber, message });
      return null;
    };

    const itemCodeLabel = (row.itemCode || row["Item Code"] || "").toString().trim();

    const quantity = parseFloat(row.quantity ?? row["Quantity"]);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return fail(`Quantity must be a number greater than 0 for item "${itemCodeLabel || "(blank)"}"`);
    }

    let store;
    let storeCodeLabel = "";
    if (isSelfDestination) {
      if (!hoSite) {
        return fail("Unable to determine the Head Office location for this PR. Please contact support.");
      }
      store = hoSite;
    } else {
      storeCodeLabel = (row.storeCode || row["Store Code"] || "").toString().trim();
      if (isCityStateRegion) {
        store = resolveStoreByCode(storeCodeLabel, resolvedHOStores, "code");
        if (!store) {
          return fail(`Store Code "${storeCodeLabel || "(blank)"}" was not found in the store master`);
        }
      } else {
        store = resolveStoreByCode(storeCodeLabel, storeList);
        if (!store || store.locationType !== "STORE") {
          return fail(`Store Code "${storeCodeLabel || "(blank)"}" was not found in the store master`);
        }
      }

      if (isDerivingFromUpload) {
        if (singleValueField) {
          const value = store[singleValueField] || "";
          if (!value) {
            return fail(
              `Store "${storeCodeLabel}" has no ${singleValueField} on record and cannot be used to target a ${procurementDestination}-destination PR`
            );
          }
          if (derivedSingleValue === null) {
            derivedSingleValue = value;
          } else if (normalizeCode(value) !== normalizeCode(derivedSingleValue)) {
            return fail(
              `Store "${storeCodeLabel}" is in ${singleValueField} "${value}", but this upload already targets ${singleValueField} "${derivedSingleValue}" (from an earlier row) — a ${procurementDestination}-destination PR can only target one ${singleValueField} per upload`
            );
          }
        }
        derivedStoresById.set(String(store._id || store.id), store);
      } else if (!selectedStoreIds.has(String(store._id || store.id))) {
        return fail(`Store "${storeCodeLabel}" is not part of the selected target stores`);
      }
    }

    const item = itemsByCode.get(normalizeCode(itemCodeLabel));
    if (!item) {
      return fail(`Item Code "${itemCodeLabel || "(blank)"}" was not found in the item master`);
    }

    // Resolves against the item's actual categoryMappings (not its deprecated single
    // category/subCategory mirror, which for a multi-mapping item may not even be the pair
    // relevant to any of the selected budget categories) -- see resolveMatchingCategoryMapping.
    const matchingMapping = resolveMatchingCategoryMapping(item, categoryIds);
    if (!matchingMapping) {
      return fail(
        `Item "${itemCodeLabel}" belongs to a budget category that is not part of the selected budget categories for this PR`
      );
    }
    const itemCategoryId = matchingMapping.category?.id ?? matchingMapping.category?._id ?? matchingMapping.category;
    const lineItem = selectedLineItems.find(
      (li) => String(li.category?.id ?? li.category?._id ?? li.category) === String(itemCategoryId)
    );
    if (!lineItem) {
      return fail(
        `Item "${itemCodeLabel}" belongs to a budget category that is not part of the selected budget categories for this PR`
      );
    }

    const storeId = store._id || store.id;
    // A Head-Office-scoped PR is raised BY Head Office — the target store/city/state/region
    // named on this row is only where the item is delivered/used, not the budget owner, so
    // its own budget is never checked. HO sites also never have their own budget document
    // (budgets only ever belong to STORE sites), so the Self destination skips this the same
    // way. Either way, the category-membership check above (against selectedLineItems, the
    // PR's own resolved budget) is the correct/sufficient guard — this per-row, per-target-
    // store re-check only still applies to a genuinely store-scoped (SINGLE_STORE) PR, which
    // never has a non-Self destination to begin with.
    if (!isSelfDestination && !isHeadOffice) {
      const cacheKey = `${storeId}:${itemCategoryId}`;
      if (!storeBudgetCache.has(cacheKey)) {
        storeBudgetCache.set(cacheKey, storeBudgetHasCategory(storeId, itemCategoryId, prType, financialYear));
      }
      const hasCategoryBudget = await storeBudgetCache.get(cacheKey);
      if (!hasCategoryBudget) {
        return fail(
          `Store "${storeCodeLabel}" does not have an approved budget for category "${
            lineItem.category?.name || lineItem.name
          }"`
        );
      }
    }

    let estimatedRate = item.price != null ? parseFloat(item.price) : NaN;
    let taxRate = item.taxPercentage != null ? parseFloat(item.taxPercentage) : 0;
    let rateContract = false;
    let vendorMappings = [];
    const approvedMappings = await fetchApprovedVendorMappings(item._id || item.id, prDate);
    if (approvedMappings.length > 0) {
      // A Vendor Item Mapping must never blindly override the Item Master price -- compare
      // the two and use whichever is higher, along with its own paired tax rate (never mix a
      // price from one source with a tax rate from the other), falling straight to the
      // vendor's price+tax when the Item Master has no price at all (estimatedRate is still
      // NaN here).
      const bestVendorMapping = approvedMappings.reduce(
        (best, m) => ((parseFloat(m.price) || 0) > (parseFloat(best.price) || 0) ? m : best),
        approvedMappings[0]
      );
      const vendorPrice = parseFloat(bestVendorMapping.price) || 0;
      const vendorTax = bestVendorMapping.taxPercentage != null ? parseFloat(bestVendorMapping.taxPercentage) || 0 : 0;
      const vendorWins = !Number.isFinite(estimatedRate) || vendorPrice >= estimatedRate;
      estimatedRate = vendorWins ? vendorPrice : estimatedRate;
      taxRate = vendorWins ? vendorTax : taxRate;
      rateContract = true;
      vendorMappings = approvedMappings;
    }
    if (!Number.isFinite(estimatedRate)) {
      return fail(
        `Unable to determine pricing for item "${itemCodeLabel}" — no item master price or approved vendor mapping found`
      );
    }

    const subCategoryId =
      matchingMapping.subCategory?._id ?? matchingMapping.subCategory?.id ?? matchingMapping.subCategory ?? null;
    const subCategoryObj = subCategoryId != null ? subCategoriesById.get(String(subCategoryId)) || null : null;

    const base = {
      ...PR_ITEM_MODEL,
      item: item._id || item.id,
      itemName: item.name || "",
      description: item.specification || "",
      hsnCode: item.hsnCode || "",
      uom: item.uom || "",
      quantity,
      estimatedRate,
      taxRate: Number.isFinite(taxRate) ? taxRate : 0,
      rateContract,
      vendorMappings,
      remarks: (row.remarks || row["Remarks"] || "").toString(),
      budgetHead: lineItem.name || "",
      // storeCode/store are this row's own resolved target -- carried through only for the
      // merge step below (isSelfDestination check, per-store allocation build); dropped
      // from the final PR item, which expresses a multi-store item's target(s) purely via
      // `allocations`, never a single flat `store` field (not a field the backend accepts).
      storeCode: store.code || storeCodeLabel,
      store: storeId,
      category: Number(itemCategoryId) || undefined,
      subCategory: subCategoryId != null ? Number(subCategoryId) : undefined,
      categoryMappingId: matchingMapping.mappingId || undefined,
      _subCategory: subCategoryObj,
      _subCategoryId: subCategoryId,
      _categoryId: Number(itemCategoryId) || undefined,
      // The resolved item object, not just its id — PRItemRow's Item Autocomplete is
      // controlled off a local `itemsForRow` list that's only ever populated by the user
      // interacting with the Subcategory dropdown, so a row that arrives pre-filled (like
      // this one) needs the full item object available as a fallback until that list loads.
      _item: item,
      _tempId: `upload-${lineItem.id}-${idx}-${item._id || item.id}`,
    };

    return { lineItemId: lineItem.id, prItem: { ...base, ...calculateItemAmounts(base) } };
  });

  // Merge rows by (lineItem, item) so that N rows targeting the same item -- one per store,
  // for a multi-store destination -- become a single PR item with one allocation per store,
  // instead of N separate items each carrying its own row's `store`. This is what makes an
  // Excel row update only its own intended store instead of the item being duplicated once
  // per store or (worse) that row's quantity being broadcast to every selected store.
  const itemGroups = new Map(); // `${lineItemId}:${itemId}` -> { lineItemId, prItem }
  resolvedRows.forEach((resolved) => {
    if (!resolved) return;
    const { lineItemId, prItem } = resolved;
    const key = `${lineItemId}:${prItem.item}`;
    const group = itemGroups.get(key);

    if (!group) {
      itemGroups.set(key, {
        lineItemId,
        prItem: isSelfDestination
          ? prItem
          : { ...prItem, allocations: buildAllocationIntent(prItem.quantity, [{ _id: prItem.store }]) },
      });
      return;
    }

    if (isSelfDestination) {
      // Single-store destination -- no per-store split needed; multiple rows for the same
      // item just add up to one larger flat quantity.
      group.prItem.quantity = (group.prItem.quantity || 0) + prItem.quantity;
    } else {
      const existingAllocation = group.prItem.allocations.find((a) => String(a.storeId) === String(prItem.store));
      if (existingAllocation) {
        // Same item + same store named again across rows -- add to that store's quantity
        // rather than silently dropping or overwriting the earlier row.
        existingAllocation.requestedQuantity += prItem.quantity;
      } else {
        group.prItem.allocations.push(...buildAllocationIntent(prItem.quantity, [{ _id: prItem.store }]));
      }
      group.prItem.quantity = getPooledQuantity(group.prItem);
    }
    Object.assign(group.prItem, calculateItemAmounts(group.prItem));
  });

  itemGroups.forEach(({ lineItemId, prItem }) => {
    delete prItem.store;
    delete prItem.storeCode;
    grouped[lineItemId] = grouped[lineItemId] || [];
    grouped[lineItemId].push(prItem);
  });

  let derivedSelection = null;
  if (isDerivingFromUpload && derivedStoresById.size > 0) {
    const derivedStores = [...derivedStoresById.values()];
    if (procurementDestination === "SPECIFIC_STORES") {
      derivedSelection = { stores: derivedStores };
    } else if (procurementDestination === "CITY") {
      derivedSelection = { cities: [...new Set(derivedStores.map((s) => s.city).filter(Boolean))] };
    } else if (procurementDestination === "STATE") {
      derivedSelection = { states: derivedSingleValue ? [derivedSingleValue] : [] };
    } else if (procurementDestination === "REGION") {
      derivedSelection = { regions: derivedSingleValue ? [derivedSingleValue] : [] };
    }
  }

  return { grouped, errors, derivedSelection };
};
